<template>
  <div class="FreeNewsroomLanding2 bg-bg font-sans">
    <!-- ===== Hero (dark cover image + primary register form) ===== -->
    <section
      :style="heroBg"
      class="border-b border-solid border-white/10 bg-surface-dark bg-cover bg-center bg-no-repeat"
      data-testid="primarySignupSection"
    >
      <div class="mx-auto max-w-screen-xl px-4 py-20 lg:py-[92px]">
        <div class="max-w-[600px]">
          <p class="mb-4.5 font-display text-xs font-bold uppercase tracking-eyebrow text-muted-soft">
            {{ $t(translationPrefix + '.section1.eyebrow') }}
          </p>

          <h1 class="mb-4 font-display text-5xl font-extrabold leading-none tracking-display text-white md:text-[52px]">
            {{ $t(translationPrefix + '.section1.title') }}
          </h1>

          <p class="mb-8 max-w-[460px] font-sans text-lg leading-relaxed text-white/70 md:text-2xl md:leading-[1.55]">
            {{ $t(translationPrefix + '.section1.text') }}
          </p>

          <sign-up-form
            v-if="!successful"
            v-model="formFields"
            :submit-register="submitRegister"
            :is-loading="isLoading"
            :v="$v.formFields"
            :submit-label="submitLabel"
            is-horizontal
            class="max-w-[580px]"
          />

          <sign-up-success-message
            v-if="successful"
            key="message"
          />

          <p
            class="mt-5 font-display text-sm font-semibold text-muted-soft"
            v-html="pageFineprint || $t(translationPrefix + '.section1.microcopy')"
          />

          <sign-up-server-errors :server-errors="serverErrors" />
        </div>
      </div>
    </section>

    <!-- ===== Distribution hub (image + copy + check bullets) ===== -->
    <section class="border-b border-solid border-line bg-surface">
      <div class="mx-auto grid max-w-screen-xl items-center gap-14 px-4 py-20 lg:grid-cols-2">
        <div>
          <img
            :src="$asset('/images/landing2/computer.png')"
            alt=""
            class="mx-auto h-auto max-w-full"
          >
        </div>
        <div>
          <h2 class="mb-3.5 font-display text-3xl font-extrabold leading-heading tracking-heading text-ink">
            {{ $t(translationPrefix + '.section2.title') }}
          </h2>

          <p class="mb-[22px] font-sans text-base leading-relaxed text-muted">
            {{ $t(translationPrefix + '.section2.text') }}
          </p>

          <ul class="flex flex-col gap-3.5">
            <li
              v-for="n in [1, 2, 3, 4]"
              :key="n"
              class="flex items-start gap-3"
            >
              <span class="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-accent-soft text-accent">
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="3"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M20 6L9 17l-5-5" />
                </svg>
              </span>
              <span class="font-sans text-base leading-snug text-ink">
                {{ $t(translationPrefix + '.section2.bullets.' + n) }}
              </span>
            </li>
          </ul>
        </div>
      </div>
    </section>

    <!-- ===== Found by journalists, media and AI (text only). Rendered only where the
         copy variant defines it, so locales not yet rewritten keep the old layout. ===== -->
    <section
      v-if="$te(translationPrefix + '.section7.title')"
      class="border-b border-solid border-line bg-white"
    >
      <div class="mx-auto grid max-w-screen-xl gap-6 px-4 py-20 lg:grid-cols-2 lg:gap-14">
        <h2 class="font-display text-3xl font-extrabold leading-heading tracking-heading text-ink">
          {{ $t(translationPrefix + '.section7.title') }}
        </h2>

        <div>
          <p class="mb-4.5 font-sans text-lg leading-relaxed text-muted">
            {{ $t(translationPrefix + '.section7.text_1') }}
          </p>

          <p class="font-sans text-lg leading-relaxed text-muted">
            {{ $t(translationPrefix + '.section7.text_2') }}
          </p>
        </div>
      </div>
    </section>

    <!-- ===== Larger context (cover image band) ===== -->
    <section
      :style="coverBg"
      class="border-b border-solid border-white/10 bg-surface-dark bg-cover bg-center bg-no-repeat"
    >
      <div class="mx-auto max-w-screen-xl px-4 py-24">
        <div class="max-w-xl">
          <h2 class="mb-4 font-display text-3xl font-extrabold leading-heading tracking-heading text-white">
            {{ $t(translationPrefix + '.section3.title') }}
          </h2>

          <p class="font-sans text-lg leading-relaxed text-white/80">
            {{ $t(translationPrefix + '.section3.text') }}
          </p>
        </div>
      </div>
    </section>

    <!-- ===== Images and videos (image + copy) ===== -->
    <section class="border-b border-solid border-line bg-white">
      <div class="mx-auto grid max-w-screen-xl items-center gap-14 px-4 py-16 lg:grid-cols-2">
        <div>
          <img
            :src="$asset('/images/for_brands/amazing_press_releases.png')"
            alt=""
            class="mx-auto h-auto max-w-full"
          >
        </div>
        <div>
          <h2 class="mb-3.5 font-display text-3xl font-extrabold leading-heading tracking-heading text-ink">
            {{ $t(translationPrefix + '.section6.title') }}
          </h2>

          <p class="font-sans text-base leading-relaxed text-muted">
            {{ $t(translationPrefix + '.section6.text') }}
          </p>
        </div>
      </div>
    </section>

    <!-- ===== Four steps ===== -->
    <section class="border-b border-solid border-line bg-bg">
      <div class="mx-auto max-w-screen-xl px-4 py-16">
        <h2 class="mb-10 text-center font-display text-3xl font-extrabold tracking-heading text-ink">
          {{ $t(translationPrefix + '.section4.title') }}
        </h2>

        <div class="grid grid-cols-2 gap-[26px] md:grid-cols-4">
          <div
            v-for="n in [1, 2, 3, 4]"
            :key="n"
            class="rounded-card border border-solid border-line bg-white p-6 shadow-sm"
          >
            <div class="mb-4 flex h-[46px] w-[46px] items-center justify-center rounded-full bg-accent font-display text-xl font-extrabold text-white">
              {{ n }}
            </div>

            <div class="mb-1.5 font-display text-base font-bold tracking-[-0.01em] text-ink">
              {{ $t(`${translationPrefix}.section4.steps.${n}.title`) }}
            </div>

            <p class="font-sans text-base leading-normal text-muted">
              {{ $t(`${translationPrefix}.section4.steps.${n}.content`) }}
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- ===== Dark CTA band (bottom register form) ===== -->
    <section class="bg-surface-dark px-4 py-16 text-center">
      <h2 class="mb-2 font-display text-4xl font-extrabold leading-heading tracking-heading text-white">
        {{ $t(translationPrefix + '.section5.title') }}
      </h2>

      <p class="mb-8 font-sans text-base text-white/60">
        {{ $t(translationPrefix + '.section5.subtitle') }}
      </p>

      <div class="mx-auto max-w-[580px]">
        <sign-up-server-errors :server-errors="serverErrors" />

        <sign-up-form
          v-if="!successful"
          v-model="formFields"
          :submit-register="submitRegister"
          :is-loading="isLoading"
          :v="$v.formFields"
          :submit-label="submitLabel"
          is-horizontal
        />

        <sign-up-success-message v-if="successful" />

        <p
          v-if="$te(translationPrefix + '.section5.microcopy')"
          class="mt-5 font-display text-sm font-semibold text-muted-soft"
        >
          {{ $t(translationPrefix + '.section5.microcopy') }}
        </p>
      </div>
    </section>
  </div>
</template>

<script>
import FreeNewsroomLandingBase from './FreeNewsroomLandingBase'

/**
 * FreeNewsroomLanding2 page. Used mostly for marketing campaigns.
 * Represents two identical pages, only texts are different. {@see module:FreeNewsroomLanding#translationPrefix}
 * @module FreeNewsroomLanding2
 */
export default {
  name: 'FreeNewsroomLanding2',

  extends: FreeNewsroomLandingBase,

  props: {
    /**
     * Passed from the route definition
     * Used only on {@see module:FreeNewsroomLanding2#gaEventName}
     */
    gaEvent: {
      type: String,
      required: true
    },

    /**
     * Passed from the route definition
     * Used only on {@see module:FreeNewsroomLanding2#translationPrefix}
     */
    transPrefix: {
      type: String,
      required: true
    }
  },

  computed: {
    /**
     * Overrides the default gaEventName from {@see module:RegisterBase#computed.gaEventName}
     */
    gaEventName () {
      return this.gaEvent
    },

    /**
     * Allows this page to have several text variants.
     * @returns {string}
     */
    translationPrefix () {
      return `pages.${this.transPrefix}`
    },

    /**
     * Submit label for both sign-up forms, when the copy variant defines one
     * (`<prefix>.cta`). Empty lets SignUpForm fall back to the generic "Register".
     * @returns {string}
     */
    submitLabel () {
      const key = `${this.translationPrefix}.cta`
      return this.$te(key) ? this.$t(key) : ''
    },

    /**
     * Hero background: the storage-hosted cover photo under a dark gradient overlay
     * (keeps the hero legible for the register form on top).
     * @returns {{ backgroundImage: string }}
     */
    heroBg () {
      return this.coverImage('/images/landing2/woman-tablet.jpg')
    },

    /**
     * "Larger context" band background: storage-hosted cover under a dark overlay.
     * @returns {{ backgroundImage: string }}
     */
    coverBg () {
      return this.coverImage('/images/landing2/landingpage-cards.jpg')
    }
  },

  methods: {
    /**
     * Build a `background-image` layering a dark overlay over a storage-hosted asset,
     * so cover-image bands read as dark surfaces regardless of the photo.
     * @param {string} path - storage path to the cover image
     * @returns {{ backgroundImage: string }}
     */
    coverImage (path) {
      const overlay = 'linear-gradient(rgba(21, 25, 26, 0.72), rgba(21, 25, 26, 0.72))'
      return { backgroundImage: `${overlay}, url("${this.$asset(path)}")` }
    }
  }
}
</script>
